import { C, names } from "@bd/content";
import { hashString } from "./rng";
import type { Company } from "./state";

/** Every name this company has used, so suggestions never repeat one. */
function usedNames(co: Company): Set<string> {
  const out = new Set<string>();
  for (const p of co.products) out.add(p.design.name);
  for (const p of co.projects) out.add(p.design.name);
  return out;
}

const pick = <T>(list: T[], k: number): T => list[((k % list.length) + list.length) % list.length];

/**
 * A name for a new game on this theme: the theme's own words in one of several forms, never one you've used.
 * `turn` moves it along, so asking again gives a different name.
 */
export function suggestGameName(co: Company, theme: string, turn: number): string {
  const used = usedNames(co);
  // the words already on your boxes: try fresh ones first, so the shelf isn't three "Watering Hole"s
  const usedWords = new Set([...used].flatMap((n) => n.toLowerCase().split(/[^a-z]+/).filter((w) => w.length > 3)));
  const fresh = (name: string) => !name.toLowerCase().split(/[^a-z]+/).some((w) => w.length > 3 && usedWords.has(w));
  const words = names.themes[theme] ?? [C.theme[theme]?.name.split(/[ &']/)[0] ?? "Game"];
  const base = hashString(`${co.id}:${theme}`);
  for (let i = 0; i < 200; i++) {
    const k = base + (turn + i) * 7919;
    const w = pick(words, k);
    const w2 = pick(words.filter((x) => x !== w), k >> 3);
    const form = pick(names.forms, k >> 5);
    // two-word theme phrases ("Once Upon", "Case File") only read well on their own with a noun
    const crowded = form.includes("&") || form.includes("{adj} {theme} {noun}");
    if (crowded && (w.includes(" ") || (w2 ?? "").includes(" "))) continue;
    const name = titleAfterColon(form
      .replace("{adj}", pick(names.adjectives, k >> 7))
      .replace("{noun}", pick(names.nouns, k >> 11))
      .replace("{place}", pick(names.places, k >> 13))
      .replace("{theme2}", w2 ?? w)
      .replace("{theme}", w));
    if (!used.has(name) && name.length <= 32 && (i >= 120 || fresh(name))) return name;
  }
  return `${pick(words, turn)} ${turn + 2}`;
}

/** A subtitle for the nth follow-up of a game (expansion, spin-off or supplement), unused so far. */
export function followUpName(co: Company, parentName: string, kind: "expansions" | "spinoffs" | "supplements", n: number): string {
  const used = usedNames(co);
  const list = names[kind];
  const join = (s: string) => titleAfterColon(kind === "spinoffs" ? `${parentName} ${s}` : `${parentName}: ${s}`);
  for (let i = 0; i < list.length; i++) {
    const name = join(pick(list, hashString(parentName) + n - 1 + i));
    if (!used.has(name)) return name;
  }
  return join(`${pick(list, n - 1)} ${n}`);
}

/** "Crypt Dash: the North" → "Crypt Dash: The North". */
function titleAfterColon(name: string): string {
  return name.replace(/: (\w)/, (_, c: string) => `: ${c.toUpperCase()}`);
}
